import React, { Suspense, lazy, useEffect, useState } from 'react'
import type { FallbackRender } from '@sentry/react'
import { SentryFallbackComponent } from '@/components/common/sentry/SentryFallbackComponent'

interface SentryStatus {
  isHealthy: boolean
  isLoaded: boolean
  error: Error | null
}

interface SentryErrorBoundaryProps {
  children: React.ReactNode
  fallback?: React.ComponentType<{
    error: Error
    componentStack: string
    eventId: string
    resetError: () => void
  }>
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
}

/**
 * Performance-optimized Sentry Error Boundary with lazy loading.
 *
 * IMPORTANT: This component does NOT block app rendering during Sentry initialization.
 *
 * Previous behavior (BLOCKING):
 * - Showed full-screen loading spinner for up to 5 seconds
 * - Performed redundant health checks
 * - Blocked all user interaction until Sentry was ready
 *
 * Current behavior (NON-BLOCKING):
 * - Renders app immediately with simple error boundary
 * - Performs lightweight availability check in background
 * - Upgrades to full Sentry error boundary when available
 * - No impact on Time to Interactive (TTI)
 */

// Lazy load Sentry components without blocking app rendering
const SentryErrorBoundaryWithFallback = lazy(
  async (): Promise<{ default: React.ComponentType<SentryErrorBoundaryProps> }> => {
    try {
      // Dynamic import of Sentry module
      const sentryModule = await import('@sentry/react')

      // Dynamic import of SentryErrorFallback
      const { SentryErrorFallback } = await import('@/components/common/sentry/SentryErrorFallback')

      // Return the Sentry ErrorBoundary component with our custom fallback
      return {
        default: ({ children, fallback, onError, ...props }: SentryErrorBoundaryProps) => {
          return (
            <sentryModule.ErrorBoundary
              fallback={(fallback || SentryErrorFallback) as FallbackRender}
              beforeCapture={(scope, error, errorInfo) => {
                scope.setContext('errorBoundary', {
                  componentStack:
                    (errorInfo as React.ErrorInfo)?.componentStack || String(errorInfo),
                  errorStack: error instanceof Error ? error.stack : undefined,
                })
                if (onError && error instanceof Error) {
                  const reactErrorInfo: React.ErrorInfo = {
                    componentStack:
                      (errorInfo as React.ErrorInfo)?.componentStack || String(errorInfo),
                  }
                  onError(error, reactErrorInfo)
                }
              }}
              {...props}
            >
              {children}
            </sentryModule.ErrorBoundary>
          )
        },
      }
    } catch (error) {
      // If Sentry loading fails, return a simple error boundary
      console.warn('[Sentry Fallback] Failed to load Sentry ErrorBoundary:', error)

      return {
        default: ({ children, onError }: SentryErrorBoundaryProps) => {
          return (
            <SimpleFallbackErrorBoundary onError={onError}>{children}</SimpleFallbackErrorBoundary>
          )
        },
      }
    }
  }
)

type SimpleFallbackErrorBoundaryProps = {
  children: React.ReactNode
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
}

// Simple React error boundary for fallback scenarios
class SimpleFallbackErrorBoundary extends React.Component<
  SimpleFallbackErrorBoundaryProps,
  { hasError: boolean; error: Error | null }
> {
  constructor(props: SimpleFallbackErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[Simple Error Boundary] Caught error:', error)
    console.error('[Simple Error Boundary] Error info:', errorInfo)

    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <SentryFallbackComponent
          error={this.state.error}
          resetError={() => this.setState({ hasError: false, error: null })}
        />
      )
    }

    return this.props.children
  }
}

// Main component that allows immediate rendering while Sentry loads in background
export const SentryErrorBoundaryLazy: React.FC<SentryErrorBoundaryProps> = ({
  children,
  fallback,
  onError,
  ...props
}) => {
  const [sentryStatus, setSentryStatus] = useState<SentryStatus>({
    isHealthy: false,
    isLoaded: false,
    error: null,
  })

  useEffect(() => {
    // Check Sentry health asynchronously without blocking rendering
    const checkSentryHealth = async () => {
      try {
        // Import Sentry config to check if it's already initialized
        const { isSentryAvailable } = await import('@/config/sentry')

        // Check if Sentry is available (this is fast and doesn't require full health check)
        const isAvailable = isSentryAvailable()

        setSentryStatus({
          isHealthy: isAvailable,
          isLoaded: true,
          error: isAvailable ? null : new Error('Sentry not available'),
        })
      } catch (error) {
        setSentryStatus({
          isHealthy: false,
          isLoaded: true,
          error: error instanceof Error ? error : new Error('Error checking Sentry availability'),
        })
      }
    }

    // Run health check in background without blocking rendering
    checkSentryHealth()
  }, [])

  // Always render children immediately - don't block on Sentry loading
  // If Sentry is not yet loaded or healthy, use simple fallback error boundary
  if (!sentryStatus.isLoaded || !sentryStatus.isHealthy) {
    return <SimpleFallbackErrorBoundary onError={onError}>{children}</SimpleFallbackErrorBoundary>
  }

  // Sentry is healthy and loaded, use lazy-loaded Sentry error boundary
  // Suspense fallback will only show if there's an actual error, not during normal loading
  return (
    <Suspense
      fallback={
        <SimpleFallbackErrorBoundary onError={onError}>{children}</SimpleFallbackErrorBoundary>
      }
    >
      <SentryErrorBoundaryWithFallback fallback={fallback} onError={onError} {...props}>
        {children}
      </SentryErrorBoundaryWithFallback>
    </Suspense>
  )
}
